import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { DomainCompleteness } from '@/lib/domainSchema'

interface DomainHeaderProps {
  label: string
  completeness: DomainCompleteness
}

const STATUS_LABEL: Record<DomainCompleteness, string> = {
  complete: 'Complete',
  partial: 'Partial',
  thin: 'Light',
  empty: 'No context yet',
}

const STATUS_DOT: Record<DomainCompleteness, string> = {
  complete: 'bg-success',
  partial: 'bg-warning',
  thin: 'bg-info',
  empty: 'bg-text-tertiary',
}

/**
 * Domain Detail page header: back link → /overview, large domain label,
 * completeness pill on the right. Status pill mirrors the dot vocabulary
 * the Overview grid uses so the visual pattern carries over the drill-in.
 */
export function DomainHeader({ label, completeness }: DomainHeaderProps) {
  return (
    <header className="flex flex-col gap-4 mb-8">
      <Link
        to="/overview"
        className="inline-flex items-center gap-1.5 text-xs text-text-tertiary hover:text-text-primary transition-colors w-fit"
      >
        <ArrowLeft size={14} />
        Back to overview
      </Link>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-display font-semibold text-text-primary tracking-tight">
          {label}
        </h1>
        <span
          data-testid="domain-completeness-pill"
          className="inline-flex items-center gap-2 text-xs text-text-tertiary"
        >
          <span
            aria-hidden
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              STATUS_DOT[completeness]
            )}
          />
          {STATUS_LABEL[completeness]}
        </span>
      </div>
    </header>
  )
}
